"use client";

/**
 * `FieldShell` — internal helper that owns the layout chrome shared by
 * every field component:
 *
 * - The `<Label>` (with optional `*` required marker)
 * - The control slot (caller-provided child)
 * - The optional `description` line (helper text)
 * - The optional inline error message
 *
 * Field components compose this so the markup stays consistent across the
 * 15 field types. The shell is intentionally NOT exported from the
 * package barrel — it is an implementation detail. Callers who want a
 * fully custom layout build their own composition with `useFormContext`
 * and the platform-ui primitives directly.
 *
 * Spacing follows the pattern documented in the plan:
 *   <div class="space-y-1.5">
 *     <Label …>
 *     {control}
 *     <p class="text-xs text-fg-muted">{description}</p>  // when set
 *     <p class="text-xs text-danger">{error}</p>          // when set
 *   </div>
 */

import * as React from "react";
import { Label } from "@aio/platform-ui";

/**
 * Props for the shared layout shell. Every field component passes its
 * raw control element as `children`; the shell owns Label + helper text +
 * error rendering. `htmlFor` MUST equal the bound input's `id` so that
 * the `<label>` associates correctly for screen readers.
 */
export interface FieldShellProps {
  /** Stable id for the underlying control. Used as `<Label htmlFor>`. */
  htmlFor: string;
  /** Visible label (already-resolved string; i18n happens upstream). */
  label: string;
  /**
   * Required indicator. We accept the boolean rather than appending the
   * `*` upstream so styling stays centralized — change here to swap for
   * a `<span class="text-danger">*</span>` if/when we want it red.
   */
  required?: boolean;
  /** Optional helper text below the control. */
  description?: string;
  /**
   * Optional inline error message. Field components read this from
   * `formState.errors[field.name]?.message` and pass it through.
   */
  error?: string;
  /** The control element (Input, Textarea, RadioGroup, …). */
  children: React.ReactNode;
}

export function FieldShell({
  htmlFor,
  label,
  required,
  description,
  error,
  children,
}: FieldShellProps): React.ReactElement {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>
        {label}
        {required ? "*" : null}
      </Label>
      {children}
      {description ? (
        <p className="text-xs text-fg-muted">{description}</p>
      ) : null}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
    </div>
  );
}
